/**
 * NavDrawer — the full-screen phone navigation drawer (Phase 14.1).
 *
 * Driven by the same `NAV_PRIMARY` constant + `useModuleAccess()` filtering
 * as `AppRail`, so phone and desktop nav can never drift. Radix Dialog gives
 * the focus trap, Esc handling and portal; tw-animate-css provides the
 * ~200ms slide. `AppRail` owns the open state and renders this beside the
 * rail; the panel itself carries `sm:hidden` so an open drawer disappears
 * if the viewport grows past the phone breakpoint.
 *
 * Source: .agents/design/responsive/responsive-app.jsx `PhoneDrawer`.
 */

import * as Dialog from '@radix-ui/react-dialog';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { BrandLogo } from './BrandLogo';
import { Icon } from './Icon';
import { companyInitials } from './AppShell';
import { NAV_PRIMARY, type NavId } from './AppRail';
import { api } from '@/lib/api';
import { clearApiToken } from '@/lib/auth';
import { useModuleAccess } from '@/lib/permissions';
import { useMeQuery } from '@/modules/dashboard/api';
import { APP_VERSION } from '@/modules/dashboard/novinky';

interface NavDrawerProps {
  active: NavId;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  version?: string;
}

export function NavDrawer({ active, open, onOpenChange, version = APP_VERSION }: NavDrawerProps) {
  const me = useMeQuery();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { access, resolved } = useModuleAccess();

  // Same render rule as AppRail: full nav until membership resolves (no flash
  // of a stripped menu), then only the modules the user may reach.
  const items = resolved ? NAV_PRIMARY.filter((item) => access.has(item.id)) : NAV_PRIMARY;

  const firmaNazev = me.data?.membership?.Nazev ?? '';
  const nick = me.data?.user?.nick ?? '';

  async function handleLogout() {
    // Mirrors UserMenuBrand.handleLogout — best-effort server revoke, the
    // local teardown is what matters.
    try {
      await api.delete('/v1/auth/logout');
    } catch {
      // ignore
    }
    clearApiToken();
    qc.clear();
    onOpenChange(false);
    navigate({ to: '/login' });
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* The panel is full-screen, so the overlay is only a safety net for
            exotic viewports; it still gives Radix its outside-click target. */}
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/20 sm:hidden" />
        <Dialog.Content
          aria-label="Hlavní navigace"
          className="fixed inset-0 z-50 flex flex-col bg-paper duration-200 data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:animate-in data-[state=open]:slide-in-from-left motion-reduce:animate-none sm:hidden"
        >
          <Dialog.Title className="sr-only">Hlavní navigace</Dialog.Title>
          <Dialog.Description className="sr-only">
            Výběr modulu a odhlášení
          </Dialog.Description>

          <div className="flex items-center gap-3 border-b border-line p-4">
            <div className="grid size-[38px] place-items-center bg-ink font-mono text-[14px] font-bold tracking-[-0.05em] text-paper">
              {companyInitials(firmaNazev || null)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-ink-3">
                Firma
              </div>
              <div className="truncate text-[14px] font-semibold text-ink">
                {firmaNazev || '—'}
              </div>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Zavřít navigaci"
                className="grid size-10 cursor-pointer place-items-center text-ink"
              >
                <Icon name="close" size={18} />
              </button>
            </Dialog.Close>
          </div>

          <nav aria-label="Hlavní navigace" className="flex-1 overflow-y-auto">
            <div className="px-5 pb-2 pt-4 font-mono text-[9.5px] uppercase tracking-[0.12em] text-ink-3">
              Moduly
            </div>
            {items.map((item) => {
              const isActive = item.id === active;
              const itemClass =
                'relative flex h-[54px] w-full items-center gap-4 px-5 active:bg-bg ' +
                (isActive ? 'text-ink' : 'text-ink-2');
              const content = (
                <>
                  <span
                    aria-hidden
                    className={'grid w-[22px] place-items-center ' + (isActive ? 'text-accent-text' : 'text-ink-3')}
                  >
                    <Icon name={item.icon} size={20} stroke={1.6} />
                  </span>
                  <span className="flex-1 text-left text-[16px] font-medium tracking-[-0.01em]">
                    {item.label}
                  </span>
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute inset-y-[11px] left-0 w-[3px] bg-accent"
                    />
                  )}
                </>
              );
              return item.to && !item.disabled ? (
                <Link
                  key={item.id}
                  to={item.to}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => onOpenChange(false)}
                  className={itemClass}
                >
                  {content}
                </Link>
              ) : (
                <button key={item.id} type="button" disabled className={itemClass + ' opacity-40'}>
                  {content}
                </button>
              );
            })}
          </nav>

          <div className="flex items-center justify-between border-t border-line px-5 py-4 font-mono text-[11px] text-ink-3">
            <span className="flex items-center gap-2.5">
              <BrandLogo height={18} />
              <span>
                {nick ? `${nick} · ` : ''}
                {version}
              </span>
            </span>
            <button
              type="button"
              onClick={() => void handleLogout()}
              className="cursor-pointer uppercase tracking-[0.08em] text-ink-2 active:text-ink"
            >
              Odhlásit
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
